'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { HiOutlineArrowRightCircle } from 'react-icons/hi2';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type BlogCardProps = {
  className?: {
    main?: string;
    link?: string;
    image?: string;
    title?: string;
    date?: string;
    contentContainer?: string;
    content?: string;
    blogImageContainer?: string;
  };
  blog: Blog;
};

export function BlogCard({ blog, className }: BlogCardProps) {
  const { t } = useTranslations();

  const locale = useLocaleStore((state) => state.currentLocale);

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <Link
      prefetch={false}
      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
      className="col-span-1 flex w-full flex-col items-start justify-start"
    >
      <div
        className={cn(
          'relative h-[230px] w-full overflow-hidden rounded-[16px] transition-all hover:opacity-90 sm:h-[245px] md:h-[260px] lg:h-[275px] xl:h-[288px]',
          className?.blogImageContainer,
        )}
      >
        {blog?.categories && blog.categories.length > 0 && (
          <span className="absolute left-4 top-4 z-10 flex h-[29px] items-center justify-center rounded-full bg-white px-4 text-xs font-medium">
            {blog.categories[0].name}
          </span>
        )}
        <Image
          src={blog?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="100vw"
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-4 px-4 pt-3 sm:pt-4">
        <div className="flex w-full flex-col items-start justify-start gap-2">
          <p className="text-xs text-[#666666] sm:text-sm lg:text-base">{formattedDate}</p>
          <h3
            data-ea-visual-tag="h5"
            className="line-clamp-2 h-12 text-lg font-medium leading-[1.3] tracking-tighter md:text-xl md:leading-[1.3]"
          >
            {blog.name}
          </h3>
        </div>
        <div className="flex items-center justify-start gap-2 transition-all hover:opacity-80">
          <p className="text-sm font-medium text-primary sm:text-base">{t('text1148')}</p>
          <HiOutlineArrowRightCircle size={24} className="text-primary" />
        </div>
      </div>
    </Link>
  );
}
